// @ts-nocheck
import React, { useCallback } from 'react';
import {
  alpha,
  Box,
  Chip,
  IconButton,
  LinearProgress,
  Popper,
  Stack,
  TextField,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material';
import PropTypes from 'prop-types';

import { isValueEmpty } from '../../../../utils/Utils';
import Multiselect from '../../Multiselect';
import { ReactComponent as FileTextIcon } from '../../../../assets/Icons/lucideIcons/file-text.svg';
import { ReactComponent as CheckCircleIcon } from '../../../../assets/Icons/lucideIcons/circle-check.svg';
import { ReactComponent as CircleXIcon } from '../../../../assets/Icons/lucideIcons/circle-x.svg';
import { ReactComponent as DownloadIcon } from '../../../../assets/Icons/lucideIcons/download.svg';
import { ReactComponent as TrashIcon } from '../../../../assets/Icons/lucideIcons/trash-2.svg';

export const FixedPopper = (props) => {
  const anchorWidth = props.anchorEl?.clientWidth;
  return (
    <Popper
      {...props}
      placement="bottom-start"
      strategy="fixed"
      style={{
        ...props.style,
        width: anchorWidth ? `${Math.max(anchorWidth, 240)}px` : 'auto',
        zIndex: 1500,
      }}
      modifiers={[
        {
          name: 'preventOverflow',
          enabled: true,
          options: { boundary: 'viewport' },
        },
        {
          name: 'flip',
          enabled: true,
          options: { fallbackPlacements: ['top-start'] },
        },
      ]}
    />
  );
};

FixedPopper.propTypes = {
  anchorEl: PropTypes.oneOfType([PropTypes.object, PropTypes.func]),
  style: PropTypes.shape({}),
};

FixedPopper.defaultProps = {
  anchorEl: undefined,
  style: {},
};

export const extractDocumentId = (doc) => {
  if (typeof doc === 'number') return doc;
  if (typeof doc === 'string') {
    if (!doc.startsWith('doc-') && !doc.includes('.')) {
      return !isNaN(Number(doc)) ? Number(doc) : doc;
    }
    return null;
  }
  if (typeof doc === 'object' && doc !== null) {
    const candidateId = doc.document_id ?? doc.id;
    if (
      candidateId != null &&
      !String(candidateId).startsWith('doc-') &&
      candidateId !== ''
    ) {
      return candidateId;
    }
  }
  return null;
};

export const FileNameCell = ({ row, mode, onPreview }) => {
  const theme = useTheme();
  const doc = row?.original;
  const isPersisted = Boolean(doc?.document_id) || mode === 'view';

  const handleClick = useCallback(() => {
    if (isPersisted && onPreview && doc) {
      onPreview(doc);
    }
  }, [isPersisted, onPreview, doc]);

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        gap: 1.25,
        minWidth: 0,
        width: '100%',
      }}
    >
      <Box
        sx={{
          width: 32,
          height: 32,
          borderRadius: '6px',
          bgcolor:
            theme.palette.selection?.main ||
            alpha(theme.palette.primary.main, 0.08),
          color: theme.palette.primary.main,
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
        }}
      >
        <FileTextIcon width={18} height={18} />
      </Box>
      <Typography
        variant="body2"
        component={isPersisted ? 'button' : 'span'}
        {...(isPersisted && { type: 'button' })}
        fontWeight={500}
        noWrap
        title={doc?.file_name}
        onClick={isPersisted ? handleClick : undefined}
        sx={{
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          minWidth: 0,
          lineHeight: 1.4,
          py: '2px',
          p: 0,
          border: 0,
          bgcolor: 'transparent',
          textAlign: 'left',
          fontFamily: 'inherit',
          maxWidth: '100%',
          display: 'block',
          ...(isPersisted && {
            color: 'primary.main',
            cursor: 'pointer',
            '&:hover': {
              textDecoration: 'underline',
              textUnderlineOffset: '2px',
            },
          }),
        }}
      >
        {doc?.file_name}
      </Typography>
    </Box>
  );
};

FileNameCell.propTypes = {
  row: PropTypes.shape({
    original: PropTypes.shape({
      document_id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      file_name: PropTypes.string,
    }),
  }),
  mode: PropTypes.string,
  onPreview: PropTypes.func,
};

FileNameCell.defaultProps = {
  row: undefined,
  mode: undefined,
  onPreview: undefined,
};

export const TagCell = ({
  row,
  mode,
  allTags,
  isDisabled,
  onDocTagsChange,
}) => {
  const theme = useTheme();
  const doc = row?.original;
  const index = row?.index;

  const handleTagsChange = useCallback(
    (val) => {
      if (typeof index !== 'number') return;
      if (isValueEmpty(val)) {
        onDocTagsChange(index, []);
      } else {
        const stringValues = val?.map((v) =>
          typeof v === 'string' ? v : v?.value || v?.label || ''
        );
        onDocTagsChange(index, stringValues);
      }
    },
    [index, onDocTagsChange]
  );

  let placeholderText = 'Select tags';
  if (doc?.tags && doc.tags.length > 0) {
    placeholderText = '';
  } else if (isDisabled) {
    placeholderText = 'No tags';
  }

  const renderMultiselectInput = useCallback(
    (params) => (
      <TextField
        {...params}
        size="small"
        variant="outlined"
        placeholder={placeholderText}
        className="no-label"
        spellCheck={true}
      />
    ),
    [placeholderText]
  );

  if (mode === 'view') {
    const docTags = Array.isArray(doc?.tags) ? doc.tags : [];
    if (docTags.length === 0) {
      return (
        <Typography variant="body2" color="text.secondary">
          -
        </Typography>
      );
    }
    const visibleTags = docTags.slice(0, 1);
    const remainingTags = docTags.slice(1);
    return (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 0.5,
          minWidth: 0,
        }}
      >
        {visibleTags.map((tag) => (
          <Chip
            key={String(tag)}
            label={tag}
            size="small"
            className="sm-chip"
            sx={{
              maxWidth: 140,
              '& .MuiChip-label': {
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              },
            }}
            title={String(tag)}
          />
        ))}
        {remainingTags.length > 0 && (
          <Tooltip title={remainingTags.join(', ')} arrow>
            <Box component="span" sx={{ display: 'inline-flex' }}>
              <Chip
                label={`+${remainingTags.length} more`}
                size="small"
                variant="outlined"
                className="sm-chip"
                sx={{
                  cursor: 'pointer',
                  bgcolor: alpha(theme.palette.text.primary, 0.04),
                  borderColor: 'divider',
                }}
              />
            </Box>
          </Tooltip>
        )}
      </Box>
    );
  }

  return (
    <Multiselect
      id={`tag-select-${doc?.id || index}`}
      options={allTags}
      value={doc?.tags || []}
      freeSolo
      forcePopupIcon={true}
      onChange={handleTagsChange}
      disableClearable={isValueEmpty(doc?.tags)}
      PopperComponent={FixedPopper}
      slots={{ popper: FixedPopper }}
      limitTags={1}
      disabled={isDisabled}
      size="small"
      renderInput={renderMultiselectInput}
    />
  );
};

TagCell.propTypes = {
  row: PropTypes.shape({
    index: PropTypes.number,
    original: PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      tags: PropTypes.arrayOf(
        PropTypes.oneOfType([PropTypes.string, PropTypes.shape({})])
      ),
    }),
  }),
  mode: PropTypes.string,
  allTags: PropTypes.arrayOf(
    PropTypes.oneOfType([PropTypes.string, PropTypes.shape({})])
  ),
  isDisabled: PropTypes.bool,
  onDocTagsChange: PropTypes.func,
};

TagCell.defaultProps = {
  row: undefined,
  mode: undefined,
  allTags: [],
  isDisabled: false,
  onDocTagsChange: () => {},
};

export const StatusCell = ({ row, mode, uploadErrors, uploadProgress }) => {
  const theme = useTheme();
  const doc = row?.original;
  const fileError = doc?.error || (doc?.key ? uploadErrors[doc.key] : null);
  const hasValidDocId = Boolean(
    doc?.document_id != null &&
    !String(doc.document_id).startsWith('doc-') &&
    doc.document_id !== ''
  );
  const isPersisted = hasValidDocId || mode === 'view';
  const fileProgress = Math.min(
    (doc?.key && uploadProgress[doc.key]) || 0,
    100
  );

  if (fileError) {
    const errorMsg =
      typeof fileError === 'string' ? fileError : doc?.error || 'Upload failed';
    const errorColor = theme.palette.error?.main;
    return (
      <Tooltip title={errorMsg} arrow>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 0.75,
            cursor: 'pointer',
            minWidth: 0,
          }}
        >
          <CircleXIcon
            width={18}
            height={18}
            style={{ color: errorColor, flexShrink: 0 }}
          />
          <Typography
            variant="body2"
            sx={{
              color: errorColor,
              fontWeight: 500,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            Failed
          </Typography>
        </Box>
      </Tooltip>
    );
  }

  if (isPersisted) {
    const verifiedColor = theme.palette.success?.main;
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
        <CheckCircleIcon
          width={18}
          height={18}
          style={{ color: verifiedColor, flexShrink: 0 }}
        />
        <Typography
          variant="body2"
          sx={{
            color: verifiedColor,
            fontWeight: 500,
          }}
        >
          Verified
        </Typography>
      </Box>
    );
  }

  if (fileProgress >= 100) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
        <Typography
          variant="body2"
          sx={{
            color: 'text.secondary',
            fontWeight: 500,
          }}
        >
          Processing...
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ width: '100%', minWidth: 0, pr: 1 }}>
      <Stack
        direction="row"
        spacing={1}
        alignItems="center"
        sx={{ width: '100%', minWidth: 0 }}
      >
        <LinearProgress
          variant="determinate"
          value={fileProgress}
          sx={{
            flex: 1,
            flexGrow: 1,
            width: '100%',
            height: 6,
            borderRadius: 3,
          }}
        />
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ minWidth: 32, textAlign: 'right', flexShrink: 0 }}
        >
          {`${Math.round(fileProgress)}%`}
        </Typography>
      </Stack>
    </Box>
  );
};

StatusCell.propTypes = {
  row: PropTypes.shape({
    original: PropTypes.shape({
      document_id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      error: PropTypes.oneOfType([PropTypes.string, PropTypes.shape({})]),
      key: PropTypes.string,
    }),
  }),
  mode: PropTypes.string,
  uploadErrors: PropTypes.shape({}),
  uploadProgress: PropTypes.shape({}),
};

StatusCell.defaultProps = {
  row: undefined,
  mode: undefined,
  uploadErrors: {},
  uploadProgress: {},
};

export const ActionsCell = ({
  row,
  mode,
  isDisabled,
  onDownload,
  onRemoveFileByIndex,
}) => {
  const doc = row?.original;
  const index = row?.index;
  const isPersisted = Boolean(doc?.document_id) || mode === 'view';

  const handleDownloadClick = useCallback(() => {
    if (doc) {
      onDownload(doc);
    }
  }, [onDownload, doc]);

  const handleRemoveClick = useCallback(() => {
    if (typeof index === 'number') {
      onRemoveFileByIndex(index, doc);
    }
  }, [onRemoveFileByIndex, index, doc]);

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'flex-end',
        gap: 0.5,
        width: '100%',
      }}
    >
      {isPersisted && (
        <IconButton
          size="small"
          title="Download document"
          onClick={handleDownloadClick}
          sx={{
            color: 'text.secondary',
            '&:hover': { color: 'primary.main' },
          }}
        >
          <DownloadIcon width={18} height={18} />
        </IconButton>
      )}
      {!isDisabled && (
        <IconButton
          size="small"
          title="Remove file"
          onClick={handleRemoveClick}
          sx={{
            color: 'text.secondary',
            '&:hover': { color: 'error.main' },
          }}
        >
          <TrashIcon width={18} height={18} />
        </IconButton>
      )}
    </Box>
  );
};

ActionsCell.propTypes = {
  row: PropTypes.shape({
    index: PropTypes.number,
    original: PropTypes.shape({
      document_id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    }),
  }),
  mode: PropTypes.string,
  isDisabled: PropTypes.bool,
  onDownload: PropTypes.func,
  onRemoveFileByIndex: PropTypes.func,
};

ActionsCell.defaultProps = {
  row: undefined,
  mode: undefined,
  isDisabled: false,
  onDownload: () => {},
  onRemoveFileByIndex: () => {},
};
